/**
 * Calculate the coverage percentage of a schedule based on required staffing
 * Optimized for single-pass calculation where possible.
 */
export function calculateCoveragePercentage(schedule, minStaffing) {
  if (!schedule?.schedule || !minStaffing) return 0;

  let totalRequired = 0;
  let totalFilled = 0;

  Object.entries(schedule.schedule).forEach(([dateStr, daySchedule]) => {
    if (!daySchedule || Object.keys(daySchedule).length === 0) return;

    const date = new Date(dateStr);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const staffingRules = isWeekend ? minStaffing.weekend : minStaffing.weekday;

    ['früh', 'zwischen', 'spät'].forEach(shift => {
      const required = staffingRules[shift] || 0;
      if (required === 0) return;

      totalRequired += required;
      const filled = daySchedule[shift]?.length || 0;
      totalFilled += Math.min(filled, required);
    });
  });

  return totalRequired === 0 ? 100 : (totalFilled / totalRequired) * 100;
}

/**
 * Get suggestions for improving schedule coverage
 * Refactored for better readability and performance.
 */
export function getCoverageImprovementSuggestions(schedule, minStaffing, employees) {
  if (!schedule?.schedule || !minStaffing || !employees) return [];

  const suggestions = [];
  const criticalDays = [];
  const employeesWithCapacity = [];
  const weekendIssues = [];
  const weekendCapacity = [];
  const distinctShiftImbalances = [];

  // 1. Analyze Days (Single Pass)
  Object.entries(schedule.schedule).forEach(([dateStr, daySchedule]) => {
    if (!daySchedule) return;

    const date = new Date(dateStr);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const staffingRules = isWeekend ? minStaffing.weekend : minStaffing.weekday;
    let issues = [];

    ['früh', 'zwischen', 'spät'].forEach(shift => {
      const required = staffingRules[shift] || 0;
      const assigned = daySchedule[shift]?.length || 0;
      if (assigned < required) {
        issues.push(`${shift}: ${assigned}/${required}`);
      }
    });

    if (issues.length > 0) {
      criticalDays.push({
        date: dateStr,
        displayDate: date.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' }),
        isWeekend,
        details: issues.join(', ')
      });
    }
  });

  if (criticalDays.length > 0) {
    suggestions.push({
      type: 'critical_days',
      title: 'Kritische Tage mit Unterbesetzung',
      days: criticalDays,
      totalCount: criticalDays.length
    });
  }

  // 2. Analyze Employees (Single Pass)
  employees.forEach(emp => {
    const empId = emp.id;
    const actualHours = schedule.employeeHours?.[empId] || 0;
    const targetHours = schedule.targetHours?.[empId] || 0;
    const stats = schedule.employeeStats?.[empId] || { früh: 0, zwischen: 0, spät: 0, totalDays: 0 };
    
    // Capacity Check (< 90%)
    if (targetHours > 0 && actualHours < targetHours * 0.9) {
      employeesWithCapacity.push({
        id: empId,
        name: emp.name,
        actualHours,
        targetHours,
        percentUsed: Math.round((actualHours / targetHours) * 100)
      });
    }

    // Weekend Check
    const weekendCount = schedule.employeeWeekendShifts?.[empId] || 0;
    const recommendedMax = emp.workload > 50 ? 2 : 1;
    
    if (weekendCount > recommendedMax) {
      weekendIssues.push({ name: emp.name, weekendShifts: weekendCount, recommendedMax });
    } else if (weekendCount < recommendedMax) {
      weekendCapacity.push({ name: emp.name, weekendShifts: weekendCount, recommendedMax });
    }

    // Shift Balance Check
    if (stats.totalDays > 5) {
      const shifts = [stats.früh, stats.zwischen, stats.spät];
      const avg = shifts.reduce((a, b) => a + b, 0) / 3;
      // Wenn eine Schicht mehr als 40% vom Durchschnitt abweicht
      const isImbalanced = shifts.some(val => Math.abs(val - avg) > (stats.totalDays * 0.4));
      
      if (isImbalanced) {
        distinctShiftImbalances.push({
          id: empId,
          name: emp.name,
          früh: Math.round((stats.früh / stats.totalDays) * 100),
          zwischen: Math.round((stats.zwischen / stats.totalDays) * 100),
          spät: Math.round((stats.spät / stats.totalDays) * 100),
          totalDays: stats.totalDays
        });
      }
    }
  });

  if (employeesWithCapacity.length > 0) {
    suggestions.push({
      type: 'available_capacity',
      title: 'Mitarbeiter mit freier Kapazität',
      employees: employeesWithCapacity
    });
  }

  if (weekendIssues.length > 0 && weekendCapacity.length > 0) {
    suggestions.push({
      type: 'weekend_redistribution',
      title: 'Mögliche Wochenendumverteilung',
      overAllocated: weekendIssues,
      hasCapacity: weekendCapacity
    });
  }

  if (distinctShiftImbalances.length > 0) {
    suggestions.push({
      type: 'shift_balance',
      title: 'Ungleichmäßige Schichtverteilung',
      employees: distinctShiftImbalances
    });
  }

  return suggestions;
}